import { useEffect, useRef, useState } from "react";

// Several public IPFS gateways, raced in parallel rather than tried one at
// a time. This used to be a strict sequential fallback (try gateway[0],
// wait for its onerror, try gateway[1], ...), but live testing against real
// WAX NFT image hashes (Sept 2026) found that most generic public gateways
// — ipfs.io, dweb.link, w3s.link, cloudflare-ipfs.com, nftstorage.link —
// now fail near-instantly for content pinned elsewhere; they simply don't
// have it, not that they're slow. gateway.pinata.cloud was the only one
// that reliably resolved every real hash tested, but takes several
// seconds. Under the old sequential scheme, a viewer sat through five fast
// failures *before* that multi-second Pinata request even started. Racing
// them means whichever gateway actually has the content wins as soon as it
// responds, and the slow-but-reliable one starts at time zero instead of
// last. gateway.pinata.cloud is listed first mainly for readability — order
// no longer affects load time now that every candidate fires at once.
const IPFS_GATEWAYS = [
  "https://gateway.pinata.cloud/ipfs/",
  "https://ipfs.io/ipfs/",
  "https://dweb.link/ipfs/",
  "https://nftstorage.link/ipfs/",
  "https://w3s.link/ipfs/",
  "https://cloudflare-ipfs.com/ipfs/",
];

// Some dead gateways neither load nor fire an error for a CID they don't
// have — they just hang. Without an overall cutoff, a genuinely broken hash
// (every gateway fails or hangs) would leave the image spinning forever
// instead of settling on the "No image" placeholder.
const RACE_TIMEOUT_MS = 15000;

function buildCandidates(hash: string | undefined): string[] {
  if (!hash) return [];
  // Already a full URL (someone's own image host, or a dedicated pinning
  // gateway a full URL was stored for) — use as-is, no gateway list to race.
  if (hash.startsWith("http")) return [hash];
  // Live debugging (Sept 2026) found visitors whose own network blocks raw
  // IPFS gateway domains outright — every direct gateway failed identically
  // and near-instantly for them, no matter how many were raced. Racing more
  // gateways can't fix a block on the *category* of domain. The same-origin
  // proxy (netlify/functions/ipfs-image.ts) fetches the image server-side
  // and streams it back from waxcreator.com's own domain, so it works
  // regardless of what the visitor's network blocks — it's listed first so
  // it usually wins the race outright, but the direct gateways stay in the
  // mix as a fallback if the proxy function itself is ever down.
  return [`/.netlify/functions/ipfs-image?hash=${hash}`, ...IPFS_GATEWAYS.map((gateway) => `${gateway}${hash}`)];
}

interface IpfsImageProps {
  /** The raw AtomicAssets media field — usually a bare IPFS hash, sometimes
   * already a full http(s) URL. Not a pre-built gateway URL. */
  hash: string | undefined;
  alt: string;
  className?: string;
  /** className for the placeholder div shown while still resolving, and
   * for the final "no image" state (no hash, or every candidate failed).
   * Defaults to the asset-card placeholder style used across the
   * marketplace. */
  placeholderClassName?: string;
  loading?: "lazy" | "eager";
}

/**
 * Renders an AtomicAssets image field by racing several IPFS gateway
 * candidates in parallel (invisibly, via plain `Image()` preloads) and
 * displaying whichever one loads first — see IPFS_GATEWAYS above for why
 * this is a race rather than a sequential fallback. Shows a placeholder
 * while resolving, and the existing "No image" placeholder if every
 * candidate fails (or there was no image field at all).
 */
export function IpfsImage({ hash, alt, className, placeholderClassName, loading }: IpfsImageProps) {
  const [resolvedSrc, setResolvedSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Gates the gateway race behind visibility instead of firing it the
  // instant every card mounts. A grid of a few dozen AssetCards used to
  // fire 6 gateway requests *each*, all at once, on page load — far more
  // simultaneous requests than a browser's per-host connection limit
  // allows, so cards near the bottom of a long grid could sit queued past
  // RACE_TIMEOUT_MS and land on "No image" despite the hash being
  // perfectly valid. IntersectionObserver defers the race until a card is
  // actually about to be seen, which keeps the number of gateways racing
  // at once roughly bounded to what's on screen (a viewport's worth of
  // cards), the way `loading="lazy"` already implied but this preloader
  // — being separate <img> elements, not the one actually in the DOM —
  // never respected on its own.
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      // No IntersectionObserver support — fall back to the old eager
      // behavior rather than never loading at all.
      setInView(true);
      return;
    }
    let settled = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          settled = true;
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" } // start the race a little before it's actually visible
    );
    observer.observe(node);
    // Safety net: IntersectionObserver's first callback is tied to the
    // browser's own rendering/layout cycle, and in some contexts (a
    // backgrounded or throttled tab, certain automation/webview hosts, or
    // just an edge case in the observer itself) that callback can be
    // delayed far longer than it takes a user to notice — or, worst case,
    // never fire at all. Before this fallback existed, that meant an image
    // stuck on "Loading…" forever with no way to recover, which is worse
    // than the original bug it was meant to fix. If the observer hasn't
    // resolved shortly after mount, start the race anyway.
    const fallbackId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      setInView(true);
      observer.disconnect();
    }, 750);
    return () => {
      settled = true;
      window.clearTimeout(fallbackId);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const candidates = inView ? buildCandidates(hash) : [];
    setResolvedSrc(null);
    setFailed(inView && candidates.length === 0);
    if (candidates.length === 0) return;

    let settled = false;
    let errorCount = 0;
    const preloaders: HTMLImageElement[] = [];

    const timeoutId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      setFailed(true);
    }, RACE_TIMEOUT_MS);

    for (const url of candidates) {
      const img = new Image();
      img.onload = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        setResolvedSrc(url);
      };
      img.onerror = () => {
        errorCount += 1;
        if (settled || errorCount < candidates.length) return;
        settled = true;
        window.clearTimeout(timeoutId);
        setFailed(true);
      };
      img.src = url;
      preloaders.push(img);
    }

    return () => {
      // Stop late-arriving events from this now-stale run (hash changed,
      // or the component unmounted) from touching state.
      settled = true;
      window.clearTimeout(timeoutId);
      for (const img of preloaders) {
        img.onload = null;
        img.onerror = null;
      }
    };
  }, [hash, inView]);

  if (resolvedSrc) {
    // Already loaded once via the preloader above, so this hits browser
    // cache and paints instantly — not a second network round trip.
    return <img src={resolvedSrc} alt={alt} className={className} loading={loading} />;
  }

  return (
    <div ref={containerRef} className={placeholderClassName ?? "asset-card__media-placeholder"}>
      {failed ? "No image" : "Loading…"}
    </div>
  );
}
